import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, Upload, CheckCircle, Clock, AlertCircle, Shield, FileText } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const ContractorOnboardPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const authToken = localStorage.getItem("token");

  // States
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [step, setStep] = useState("loading"); // loading, set-password, login-redirect, insurance, pending, rejected
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Insurance form
  const [insurerName, setInsurerName] = useState("");
  const [policyNumber, setPolicyNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Status data
  const [statusData, setStatusData] = useState(null);

  useEffect(() => {
    if (token) {
      validateInvite();
    } else if (authToken) {
      checkOnboardingStatus();
    } else {
      setStep("login-redirect");
      setLoading(false);
    }
  }, [token, authToken]);

  const validateInvite = async () => {
    try {
      const response = await axios.get(`${API}/api/contractor-management/invite/${token}`);
      setInviteData(response.data);
      setStep("set-password");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid or expired invite");
      setStep("login-redirect");
    } finally {
      setLoading(false);
    }
  };

  const checkOnboardingStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${authToken}` };
      const response = await axios.get(`${API}/api/contractor-management/insurance/status`, { headers });
      setStatusData(response.data);

      const status = response.data.status;
      if (status === "active") {
        navigate("/contractor");
      } else if (status === "onboarding" || status === "invited") {
        setStep("insurance");
      } else if (status === "pending_approval") {
        setStep("pending");
      } else if (status === "rejected") {
        setStep("rejected");
      } else if (status === "suspended") {
        setStep("suspended");
      } else {
        navigate("/contractor");
      }
    } catch (error) {
      console.error("Status check error:", error);
      // If 403, check the error message for specific status
      const detail = error.response?.data?.detail || "";
      if (detail.includes("CONTRACTOR_SUSPENDED")) {
        setStep("suspended");
      } else if (detail.includes("CONTRACTOR_PENDING")) {
        setStep("pending");
      } else if (detail.includes("CONTRACTOR_REJECTED")) {
        setStep("rejected");
      } else if (detail.includes("CONTRACTOR_ONBOARDING")) {
        setStep("insurance");
      } else {
        // If not a contractor or no record, redirect to login
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/contractor-management/accept-invite?token=${token}&password=${password}`);
      toast.success("Account created! Please log in to continue.");
      navigate("/login");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  };

  const handleInsuranceUpload = async (e) => {
    e.preventDefault();
    if (!insuranceFile) {
      toast.error("Please select a file to upload");
      return;
    }
    if (!insurerName || !policyNumber || !expiryDate) {
      toast.error("Please fill in all insurance details");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", insuranceFile);
      formData.append("insurer_name", insurerName);
      formData.append("policy_number", policyNumber);
      formData.append("expiry_date", expiryDate);

      const headers = { 
        Authorization: `Bearer ${authToken}`,
        "Content-Type": "multipart/form-data"
      };
      
      await axios.post(`${API}/api/contractor-management/insurance/upload`, formData, { headers });
      toast.success("Insurance uploaded! Awaiting approval.");
      setStep("pending");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload insurance");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-800">Goodacres</h1>
          <p className="text-slate-500">Contractor Portal</p>
        </div>

        {/* Set Password Step */}
        {step === "set-password" && inviteData && (
          <Card>
            <CardHeader>
              <CardTitle>Welcome, {inviteData.name}!</CardTitle>
              <CardDescription>Set your password to create your account</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSetPassword} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input value={inviteData.email} disabled className="bg-slate-50" />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    required
                  />
                </div>
                <div>
                  <Label>Confirm Password</Label>
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Account
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Insurance Upload Step */}
        {step === "insurance" && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Shield className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <CardTitle>Insurance Required</CardTitle>
                  <CardDescription>Upload your Public Liability Insurance to continue</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleInsuranceUpload} className="space-y-4">
                <div>
                  <Label>Insurer Name *</Label>
                  <Input
                    value={insurerName}
                    onChange={(e) => setInsurerName(e.target.value)}
                    placeholder="e.g. Aviva, AXA"
                    required
                  />
                </div>
                <div>
                  <Label>Policy Number *</Label>
                  <Input
                    value={policyNumber}
                    onChange={(e) => setPolicyNumber(e.target.value)}
                    placeholder="Your policy number"
                    required
                  />
                </div>
                <div>
                  <Label>Expiry Date *</Label>
                  <Input
                    type="date"
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>
                <div>
                  <Label>Insurance Document (PDF) *</Label>
                  <div className="mt-1">
                    <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {insuranceFile ? (
                          <>
                            <FileText className="w-8 h-8 text-green-500 mb-2" />
                            <p className="text-sm text-slate-600">{insuranceFile.name}</p>
                          </>
                        ) : (
                          <>
                            <Upload className="w-8 h-8 text-slate-400 mb-2" />
                            <p className="text-sm text-slate-500">Click to upload</p>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => setInsuranceFile(e.target.files[0])}
                      />
                    </label>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={uploading}>
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                  Submit for Approval
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Pending Approval Step */}
        {step === "pending" && (
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto p-3 bg-amber-100 rounded-full w-fit mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <CardTitle>Awaiting Approval</CardTitle>
              <CardDescription>
                Your insurance documents have been submitted and are being reviewed.
              </CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-slate-600 mb-4">
                You'll receive an email once your account has been approved. This usually takes 1-2 business days.
              </p>
              <Button variant="outline" onClick={() => checkOnboardingStatus()}>
                Check Status
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Rejected Step */}
        {step === "rejected" && (
          <Card className="border-red-200">
            <CardHeader className="text-center">
              <div className="mx-auto p-3 bg-red-100 rounded-full w-fit mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-red-700">Insurance Rejected</CardTitle>
              <CardDescription>
                Your insurance submission was not approved.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {statusData?.rejection_notes && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-red-700">
                    <strong>Reason:</strong> {statusData.rejection_notes}
                  </p>
                </div>
              )}
              <Button className="w-full" onClick={() => setStep("insurance")}>
                Upload New Documents
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Suspended Step */}
        {step === "suspended" && (
          <Card className="border-orange-200">
            <CardHeader className="text-center">
              <div className="mx-auto p-3 bg-orange-100 rounded-full w-fit mb-4">
                <Shield className="w-8 h-8 text-orange-600" />
              </div>
              <CardTitle className="text-orange-700">Account Suspended</CardTitle>
              <CardDescription>
                Your account has been temporarily suspended.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-orange-700">
                  This is usually due to expired insurance or missing documentation.
                  Please upload valid insurance documents to reactivate your account.
                </p>
              </div>
              <Button className="w-full" onClick={() => setStep("insurance")}>
                Upload New Insurance
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Login Redirect */}
        {step === "login-redirect" && (
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Contractor Portal</CardTitle>
              <CardDescription>Please log in to continue</CardDescription>
            </CardHeader>
            <CardContent className="text-center">
              <Button onClick={() => navigate("/login")} className="w-full">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ContractorOnboardPage;
